import React, { useEffect, useState } from 'react';
import {
  Alert,
  Checkbox,
  DatePicker,
  Form,
  Modal,
  Spin,
  Switch,
  Tabs,
  Typography,
  message,
} from 'antd';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { fromIstWallClock, istDateTime, toIstWallClock } from '@/utils/cricketCalendar';
import {
  LANGUAGES,
  LANGUAGE_LABEL,
  MOMENT_LABEL,
  RESULT_COPY_NOTE,
  customCopyMode,
  planLanguages,
} from '@/utils/matchPushCopy';
import type {
  MatchPushPlan,
  MatchPushSettings,
  MatchPushTemplate,
  PushLanguage,
  TemplateMoment,
} from '@/types/matchPushes';
import { copyDraftFrom, copyOverrideFrom, planUpdateBody } from '@/utils/planEdit';
import type { CopyDraft } from '@/utils/planEdit';
import { CopyEditor } from './CopyEditor';
import type { CopyValue } from './CopyEditor';

const { Text } = Typography;

interface Props {
  plan: MatchPushPlan | null;
  settings: MatchPushSettings | null;
  onClose: () => void;
  onSaved: (plan: MatchPushPlan) => void;
}

/** Template moment whose text is a plan's starting point for custom copy. */
const templateMoment = (plan: MatchPushPlan): TemplateMoment =>
  plan.moment === 'result' ? 'result_win' : plan.moment;

/** Reschedule a fixed plan and/or give it custom copy for this match only. */
export const PlanEditModal: React.FC<Props> = ({ plan, settings, onClose, onSaved }) => {
  const [sendAt, setSendAt] = useState<ReturnType<typeof toIstWallClock> | null>(null);
  const [custom, setCustom] = useState(false);
  const [copy, setCopy] = useState<CopyDraft>(copyDraftFrom(null));
  const [templates, setTemplates] = useState<MatchPushTemplate[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!plan) return;
    setSendAt(plan.scheduledAt ? toIstWallClock(plan.scheduledAt) : null);
    setCustom(plan.copyOverride !== null);
    setCopy(copyDraftFrom(plan.copyOverride));
    setTemplates(null);
    let cancelled = false;
    adminApi.matchPushes
      .listTemplates()
      .then((response) => {
        if (!cancelled) setTemplates(response.data.items);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [plan]);

  if (!plan) return null;

  const canReschedule =
    plan.kind === 'fixed' && plan.status !== 'sent' && plan.status !== 'sending';

  /** Starting text for a language: the active template of this moment, else empty. */
  const seed = (language: PushLanguage): CopyValue => {
    const found = templates?.find(
      (t) => t.active && t.moment === templateMoment(plan) && t.language === language
    );
    return found ? { title: found.title, body: found.body } : { title: '', body: '' };
  };

  // The languages this plan can send, plus any the stored override already holds.
  const usable = planLanguages(plan.audienceCountries, settings?.languageMode ?? 'experiment');
  const languages = LANGUAGES.filter((l) => usable.includes(l) || plan.copyOverride?.[l]);

  const turnOnCustom = (on: boolean) => {
    setCustom(on);
    if (on && !plan.copyOverride) {
      setCopy((c) => ({ ...c, ...Object.fromEntries(languages.map((l) => [l, seed(l)])) }));
    }
  };

  const mode = customCopyMode(plan);
  const editing = custom && mode === 'editable';
  const { override, problems } = editing
    ? copyOverrideFrom(copy, languages)
    : { override: {}, problems: [] };

  const save = async () => {
    const body = planUpdateBody(plan, {
      canReschedule,
      sendAtIso: sendAt ? fromIstWallClock(sendAt) : null,
      custom,
      copy: override,
    });
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const response = await adminApi.matchPushes.updatePlan(plan.id, body);
      // A note means the change did not do what was asked.
      if (response.data.note) message.warning(response.data.note, 8);
      else message.success('Saved');
      onSaved({ ...plan, ...response.data, fixture: response.data.fixture ?? plan.fixture });
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to save the changes'));
    } finally {
      setSaving(false);
    }
  };

  const sendWindow =
    settings &&
    `${String(settings.windowStartHour).padStart(2, '0')}:00 to ${String(settings.windowEndHour).padStart(2, '0')}:00 IST`;

  return (
    <Modal
      open
      width={680}
      title={`Edit: ${MOMENT_LABEL[plan.moment]}, ${plan.fixture.team1Name} vs ${plan.fixture.team2Name}`}
      okText="Save"
      okButtonProps={{ disabled: problems.length > 0, loading: saving }}
      onOk={save}
      onCancel={onClose}
      destroyOnClose
    >
      <Form layout="vertical">
        {canReschedule ? (
          <Form.Item
            label="Send time (IST)"
            extra={
              sendWindow
                ? `Must be inside the send window, ${sendWindow}. Match starts ${istDateTime(plan.fixture.startsAt)}.`
                : `Match starts ${istDateTime(plan.fixture.startsAt)}.`
            }
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD MMM YYYY HH:mm"
              value={sendAt}
              onChange={(value) => setSendAt(value)}
              allowClear={false}
              style={{ width: 240 }}
            />
          </Form.Item>
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              plan.kind === 'live'
                ? 'This push goes out when the moment is seen in the match, so it has no fixed time.'
                : 'This push has already gone out.'
            }
          />
        )}
        {mode === 'editable' ? (
          <Form.Item
            label="Copy"
            extra="Custom copy replaces the text of every version in that language, for this match only."
          >
            <Switch checked={custom} onChange={turnOnCustom} />{' '}
            <Text>{custom ? 'Custom copy for this match' : 'Use the copy from the Copy tab'}</Text>
          </Form.Item>
        ) : (
          <Form.Item label="Copy">
            <Alert type="info" showIcon message={RESULT_COPY_NOTE} />
            {mode === 'clear-only' && (
              <Checkbox
                style={{ marginTop: 8 }}
                checked={!custom}
                onChange={(e) => setCustom(!e.target.checked)}
              >
                Remove the custom copy this push still has
              </Checkbox>
            )}
          </Form.Item>
        )}
        {editing &&
          (templates === null ? (
            <Spin />
          ) : (
            <Tabs
              items={languages.map((language) => ({
                key: language,
                label: LANGUAGE_LABEL[language],
                children: (
                  <CopyEditor
                    language={language}
                    value={copy[language]}
                    optional
                    onChange={(value) => setCopy((c) => ({ ...c, [language]: value }))}
                  />
                ),
              }))}
            />
          ))}
        {problems.length > 0 && (
          <Alert
            type="error"
            showIcon
            message="Fix the copy before saving"
            description={problems.join('. ')}
          />
        )}
      </Form>
    </Modal>
  );
};
